/**
 * AdminStarRating Component
 *
 * Interactive 5-star rating widget for admin/evaluator campaign ratings.
 * Replaces the previous A/B/C/D dropdown with a visual star-based system.
 *
 * Rating interpretation:
 * - 5 stars = Very Low Risk (Green)
 * - 4 stars = Low Risk (Light Green)
 * - 3 stars = Moderate Risk (Yellow)
 * - 2 stars = High Risk (Orange)
 * - 1 star  = Very High Risk (Red)
 *
 * @deprecated_feature Replaces: A/B/C/D dropdown in Campaignview.jsx (lines 906-924)
 */

import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, Typography, Rating, TextField, Button, CircularProgress } from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import { submitAdminRating } from "../../redux/actions/dealFlowActions";
import "./AdminStarRating.scss";

// Risk level labels based on star rating
const riskLabels = {
  1: { text: "Very High Risk", color: "#d32f2f" },
  2: { text: "High Risk", color: "#f57c00" },
  3: { text: "Moderate Risk", color: "#fbc02d" },
  4: { text: "Low Risk", color: "#7cb342" },
  5: { text: "Very Low Risk", color: "#388e3c" },
};

const AdminStarRating = ({
  campaignId,
  initialRating = 0,
  initialNotes = "",
  onRatingSubmit,
  disabled = false,
  compact = false,
}) => {
  const dispatch = useDispatch();
  const { submitting } = useSelector((state) => state.dealFlow);

  const [rating, setRating] = useState(initialRating);
  const [hoverRating, setHoverRating] = useState(-1);
  const [notes, setNotes] = useState(initialNotes);
  const [hasChanges, setHasChanges] = useState(false);

  // Update local state if props change (e.g., after fetching existing rating)
  useEffect(() => {
    setRating(initialRating);
    setNotes(initialNotes);
    setHasChanges(false);
  }, [initialRating, initialNotes]);

  const handleRatingChange = (event, newValue) => {
    if (disabled) return;
    setRating(newValue);
    setHasChanges(true);
  };

  const handleNotesChange = (event) => {
    if (disabled) return;
    setNotes(event.target.value);
    setHasChanges(true);
  };

  const handleSubmit = async () => {
    if (!rating || rating < 1 || rating > 5) {
      return;
    }

    try {
      await dispatch(submitAdminRating(campaignId, rating, notes));
      setHasChanges(false);
      if (onRatingSubmit) {
        onRatingSubmit(rating, notes);
      }
    } catch (error) {
      // Error handling done in action
    }
  };

  // Determine which rating to show for risk label
  const displayRating = hoverRating !== -1 ? hoverRating : rating;
  const riskInfo = displayRating > 0 ? riskLabels[displayRating] : null;

  // Compact mode for list views
  if (compact) {
    return (
      <Box className="admin-star-rating admin-star-rating--compact">
        <Rating
          value={rating}
          onChange={handleRatingChange}
          onChangeActive={(event, newHover) => setHoverRating(newHover)}
          disabled={disabled || submitting}
          precision={1}
          size="small"
          emptyIcon={<StarIcon style={{ opacity: 0.3 }} fontSize="inherit" />}
        />
        {riskInfo && (
          <Typography
            variant="caption"
            className="admin-star-rating__risk-label"
            style={{ color: riskInfo.color }}
          >
            {riskInfo.text}
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <Box className="admin-star-rating">
      <Typography variant="subtitle2" className="admin-star-rating__title">
        Campaign Rating
      </Typography>

      <Box className="admin-star-rating__stars-container">
        <Rating
          value={rating}
          onChange={handleRatingChange}
          onChangeActive={(event, newHover) => setHoverRating(newHover)}
          disabled={disabled || submitting}
          precision={1}
          size="large"
          emptyIcon={<StarIcon style={{ opacity: 0.3 }} fontSize="inherit" />}
          sx={{
            "& .MuiRating-iconFilled": {
              color: riskInfo?.color || "#faaf00",
            },
            "& .MuiRating-iconHover": {
              color: riskInfo?.color || "#faaf00",
            },
          }}
        />

        {riskInfo && (
          <Typography
            variant="body2"
            className="admin-star-rating__risk-label"
            style={{ color: riskInfo.color, fontWeight: 600 }}
          >
            {riskInfo.text}
          </Typography>
        )}
      </Box>

      <Typography variant="caption" className="admin-star-rating__helper">
        5 stars = Low Risk | 1 star = High Risk
      </Typography>

      {!disabled && (
        <>
          <TextField
            label="Rating Notes (optional)"
            multiline
            rows={3}
            value={notes}
            onChange={handleNotesChange}
            disabled={submitting}
            fullWidth
            variant="outlined"
            placeholder="Add justification for your rating..."
            className="admin-star-rating__notes"
            sx={{ marginTop: 2 }}
          />

          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={!rating || submitting || !hasChanges}
            className="admin-star-rating__submit"
            sx={{ marginTop: 2 }}
          >
            {submitting ? (
              <>
                <CircularProgress size={20} sx={{ marginRight: 1 }} />
                Submitting...
              </>
            ) : hasChanges ? (
              "Submit Rating"
            ) : (
              "Rating Saved"
            )}
          </Button>
        </>
      )}
    </Box>
  );
};

export default AdminStarRating;
